/**
 * DrawingUpload — tekening/PDF uploaden en analyseren, voorstel op **bestaand** project (PUT drawing_proposal).
 * Analyse-cache op het dossier tot nieuwe run, wissen, of alle posten geïmporteerd; gedeeltelijke import vult het conceptvoorstel aan.
 */
import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Upload, FileText, Loader2, Info } from 'lucide-react';
import { api } from '../lib/api';
import type {
  DrawingAnalysisCache,
  DrawingProposal,
  ProjectLibraryDocument,
  ProjectSystemLine,
} from '../types/project';
import { libraryDocumentsToAnalyzeRefs } from '../lib/prepare-library-for-analysis';
import { Tooltip } from './ui/Tooltip';
import { CUSTOMER_TYPE_OPTIONS, DEFAULT_CALC_INDEX_YEAR } from '../lib/customer-types';
import { getDrawingAnalysisAudienceHint } from '../lib/customer-profiles';
import {
  DEFAULT_IMPORT_PRESET,
  importFilterHintForCustomerType,
  importPresetLabel,
  importTierBadgeClass,
  importTierLabelNl,
  normalizeImportTier,
  tierMatchesPreset,
  type ImportFilterPreset,
  type ImportTier,
} from '../lib/drawing-import-tier';
import { getJourneyProfileHint } from '../lib/project-flow-copy';

function formatInventoryMeasure(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—';
  if (Number.isInteger(n)) return String(n);
  const r = Math.round(n * 100) / 100;
  return String(r).replace('.', ',');
}

function truncateInvText(s: string | null | undefined, max = 40): string {
  const t = (s ?? '').trim();
  if (!t) return '';
  return t.length <= max ? t : `${t.slice(0, max - 1)}…`;
}
import { isAcceptedImageOrPdfFile } from '../lib/accept-upload-files';
import {
  betrouwbaarheidAlsNlTekst,
  formatTechnischeIdVoorWeergave,
  hoeveelheidTekst,
} from '../lib/drawing-analysis-display';
import { openAssistantWithDraft } from '../lib/assistantBus';
import { pdfToImageFiles } from '../lib/pdf-utils';

function normalizePostalCode(raw: string): string {
  return raw.replace(/\s+/g, '').toUpperCase();
}

const ACCEPT = 'image/png,image/jpeg,image/webp,image/gif,application/pdf';
const MAX_FILES = 5;
/** Zelfde limiet als in UI-tekst; te grote PDF’s falen vaak al bij conversie/analyse. */
const MAX_FILE_BYTES = 10 * 1024 * 1024;
/** Zelfde limiet als API /analyze_documents */
const MAX_ANALYZE_DOCUMENTS = 10;

/** Roteert tijdens analyse — indicatie voor de gebruiker, geen echte serverstappen. */
const ANALYSIS_NARRATIVE_STEPS = [
  'Documenten klaarzetten en uploaden…',
  'Legenda en titelblok bekijken…',
  'Plattegrond en maatvoering doorlopen…',
  'Ruimtes, verdiepingen en zones inventariseren…',
  'Installaties en bouwdelen herkennen…',
  'Koppelen aan je prijsboek-catalogus…',
  'Voorstelregels en samenvatting opbouwen…',
] as const;

export interface DrawingUploadProjectProps {
  projectId: string;
  tenantId?: string;
  projectType: string;
  buildingType: string;
  postalCode?: string;
  customerTypeId: string;
  /** Prijspeil (meestal uit projectcontext) */
  indexYear?: string;
  /** Laatst opgeslagen analyse op het dossier (server) */
  persistedAnalysisCache?: DrawingAnalysisCache | null;
  /** Na succesvol koppelen van het voorstel of na opslaan/wissen analyse-cache */
  onProposalAttached?: () => void;
  /** Na upload naar storage: voeg toe aan dossier-bibliotheek (parallel aan analyse) */
  onAfterUploadToLibrary?: (
    items: Array<{ url: string; label?: string; content_type?: string }>
  ) => Promise<void>;
  /** Voor If-Match bij PUT dossier (optimistische locking) */
  revisionSeq?: number;
  /** Actuele revisie uit React Query — voorkomt 412 na parallelle saves (regio, bibliotheek). */
  getRevisionSeq?: () => number;
  /** Documenten uit de dossier-bibliotheek (tab Gegevens) om te hergebruiken voor analyse */
  libraryDocuments?: ProjectLibraryDocument[];
  /** Bestaand concept-tekeningvoorstel — gedeeltelijke import wordt hieraan toegevoegd */
  existingDrawingProposal?: DrawingProposal | null;
  /** Dossier-template (project.template_id) — mee naar analyse voor scope/posten-hint */
  templateId?: string;
}

interface AnalysisSystemLine {
  system_id: string;
  variant_id: string | null;
  quantity_hint: number | null;
  confidence?: number;
  hoofdgroep_nl?: string;
  subgroep_nl?: string;
  detail_nl?: string;
  bouwcluster?: string | null;
  verdieping?: string | null;
  ruimte_of_nummer?: string | null;
  reasoning?: string;
  /** Advies voor importfilter (API / model) */
  import_tier?: ImportTier | string | null;
}

interface AnalysisResult {
  resolved: boolean;
  confidence: number;
  intent: {
    systems: AnalysisSystemLine[];
    quantity_estimates: Record<string, number> | null;
  };
  analysis_summary: string | null;
  clarification_questions: string[];
  document_insights?: Array<{ doc_index?: number; findings?: string[] }>;
  analysis_depth?: 'standard' | 'deep' | 'full';
  drawing_extras?: {
    overige_waarneembare_elementen?: string[];
    afkortingen_gezien?: Array<{ code?: string; betekenis_nl?: string }>;
    ruimtes_inventaris?: Array<{
      woning_of_eenheid: string;
      verdieping: string;
      ruimtenummer?: string | null;
      ruimte_naam: string;
      doc_index?: number | null;
      opmerking?: string | null;
      oppervlakte_m2?: number | null;
      wandlengte_totaal_m1?: number | null;
      plafondhoogte_m?: number | null;
      aantal_deuren?: number | null;
      deuren_omschrijving?: string | null;
      aantal_raam_kozijn?: number | null;
      kozijnen_omschrijving?: string | null;
      meetwaarden_bron?: 'tekening' | 'geschat' | null;
    }>;
  };
  analysis_meta?: {
    ruimtes_inventory_completion_applied?: boolean;
    ruimtes_count_pass1?: number;
    ruimtes_count_final?: number;
    ruimtes_threshold_reference?: number | null;
    inventory_engine_warnings_nl?: string[];
    inventory_conflicts?: Array<{
      doc_index: number;
      ruimtenummer: string;
      ruimte_namen: string[];
      message_nl: string;
    }>;
    systems_inventory_autofill_count?: number;
    systems_inventory_autofill_note_nl?: string | null;
  };
  audit_id?: string;
}

function analysisResultToCache(r: AnalysisResult): DrawingAnalysisCache {
  return {
    saved_at: new Date().toISOString(),
    audit_id: r.audit_id,
    resolved: r.resolved,
    confidence: r.confidence,
    analysis_depth: r.analysis_depth ?? 'full',
    analysis_summary: r.analysis_summary,
    clarification_questions: r.clarification_questions ?? [],
    document_insights: r.document_insights,
    intent: {
      systems: r.intent.systems as unknown[],
      quantity_estimates: r.intent.quantity_estimates,
    },
    analysis_meta: r.analysis_meta,
    drawing_extras: r.drawing_extras,
  };
}

function cacheToAnalysisResult(c: DrawingAnalysisCache): AnalysisResult {
  return {
    resolved: c.resolved ?? false,
    confidence: c.confidence,
    intent: {
      systems: (c.intent.systems ?? []) as AnalysisSystemLine[],
      quantity_estimates: c.intent.quantity_estimates ?? null,
    },
    analysis_summary: c.analysis_summary,
    clarification_questions: c.clarification_questions ?? [],
    document_insights: c.document_insights,
    analysis_depth: c.analysis_depth,
    analysis_meta: c.analysis_meta,
    drawing_extras: c.drawing_extras,
    audit_id: c.audit_id,
  };
}

function ensureLineId(line: ProjectSystemLine): ProjectSystemLine {
  if (line.line_id?.trim()) return line;
  return { ...line, line_id: `line_${Date.now()}_${Math.random().toString(36).slice(2, 11)}` };
}

function mergeDraftProposal(
  existing: DrawingProposal | null | undefined,
  incomingLines: ProjectSystemLine[],
  auditId: string | undefined,
  summary: string | null
): DrawingProposal {
  const prev =
    existing?.status === 'draft' && Array.isArray(existing.proposed_systems)
      ? existing.proposed_systems.map(ensureLineId)
      : [];
  const nextLines = incomingLines.map(ensureLineId);
  return {
    status: 'draft',
    audit_id: auditId,
    analysis_summary: summary,
    proposed_systems: [...prev, ...nextLines],
    created_at:
      existing?.status === 'draft' && existing.created_at ? existing.created_at : new Date().toISOString(),
  };
}

function trimAnalysisAfterImport(r: AnalysisResult, importedIndices: Set<number>): AnalysisResult | null {
  const systems = r.intent.systems.filter((_, i) => !importedIndices.has(i)) as AnalysisSystemLine[];
  if (systems.length === 0) return null;
  const keptIds = new Set(systems.map((s) => s.system_id).filter(Boolean));
  const est = r.intent.quantity_estimates;
  let newEst: Record<string, number> | null = null;
  if (est && typeof est === 'object') {
    const o: Record<string, number> = {};
    for (const k of Object.keys(est)) {
      if (keptIds.has(k)) o[k] = est[k];
    }
    if (Object.keys(o).length > 0) newEst = o;
  }
  return {
    ...r,
    intent: { systems, quantity_estimates: newEst },
  };
}

function libraryEntryAnalyzable(d: ProjectLibraryDocument): boolean {
  const ct = (d.content_type ?? '').toLowerCase();
  if (ct.startsWith('image/') || ct.includes('pdf')) return true;
  return /\.(png|jpe?g|webp|gif|pdf)$/i.test(d.label ?? '');
}

export function DrawingUpload({
  projectId,
  tenantId = 'default',
  projectType,
  buildingType,
  postalCode = '',
  customerTypeId,
  indexYear = DEFAULT_CALC_INDEX_YEAR,
  persistedAnalysisCache = null,
  onProposalAttached,
  onAfterUploadToLibrary,
  revisionSeq = 1,
  getRevisionSeq,
  libraryDocuments = [],
  existingDrawingProposal = null,
  templateId,
}: DrawingUploadProjectProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [customerWishes, setCustomerWishes] = useState('');
  const [loading, setLoading] = useState(false);
  /** Alleen tijdens tekeninganalyse — niet bij import of cache wissen. */
  const [showAnalyzeNarrative, setShowAnalyzeNarrative] = useState(false);
  const [analyzeNarrativeIdx, setAnalyzeNarrativeIdx] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AnalysisResult | null>(null);
  /** Welke voorgestelde posten meenemen bij importeren (index in intent.systems) */
  const [selectedLineIdx, setSelectedLineIdx] = useState<Set<number>>(new Set());
  /** Filteradvies vóór import: welke import_tier-lagen meenemen */
  const [importPreset, setImportPreset] = useState<ImportFilterPreset>(DEFAULT_IMPORT_PRESET);
  const importPresetRef = useRef(importPreset);
  importPresetRef.current = importPreset;
  const prevAuditIdRef = useRef<string | undefined>(undefined);
  const prevSystemsLenRef = useRef<number>(-1);
  /** Welke bibliotheekdocumenten mee in dezelfde analyse-run */
  const [selectedLibraryDocIds, setSelectedLibraryDocIds] = useState<Set<string>>(new Set());

  const rev = () => getRevisionSeq?.() ?? revisionSeq;

  const cacheHydrateKey = `${projectId}|${persistedAnalysisCache?.saved_at ?? '—'}|${persistedAnalysisCache?.audit_id ?? ''}`;

  useEffect(() => {
    setResult(null);
    setFiles([]);
    setSelectedLibraryDocIds(new Set());
    prevAuditIdRef.current = undefined;
    prevSystemsLenRef.current = -1;
  }, [projectId]);

  useEffect(() => {
    if (!showAnalyzeNarrative) {
      setAnalyzeNarrativeIdx(0);
      return;
    }
    setAnalyzeNarrativeIdx(0);
    const id = window.setInterval(() => {
      setAnalyzeNarrativeIdx((i) => (i + 1) % ANALYSIS_NARRATIVE_STEPS.length);
    }, 2700);
    return () => window.clearInterval(id);
  }, [showAnalyzeNarrative]);

  const analyzableLibrary = useMemo(
    () => libraryDocuments.filter(libraryEntryAnalyzable),
    [libraryDocuments]
  );

  useEffect(() => {
    if (!persistedAnalysisCache) return;
    setResult(cacheToAnalysisResult(persistedAnalysisCache));
  }, [cacheHydrateKey, persistedAnalysisCache]);

  const applySelectionForPreset = useCallback((res: AnalysisResult, preset: ImportFilterPreset) => {
    const systems = res.intent.systems ?? [];
    const next = new Set<number>();
    systems.forEach((raw, i) => {
      const tier = normalizeImportTier((raw as AnalysisSystemLine).import_tier);
      if (tierMatchesPreset(tier, preset)) next.add(i);
    });
    setSelectedLineIdx(next);
  }, []);

  useEffect(() => {
    const aid = result?.audit_id;
    const n = result?.intent?.systems?.length ?? 0;
    if (!result || n === 0) {
      setSelectedLineIdx(new Set());
      if (n === 0) prevSystemsLenRef.current = 0;
      return;
    }
    if (prevAuditIdRef.current !== aid) {
      prevAuditIdRef.current = aid;
      prevSystemsLenRef.current = n;
      setImportPreset(DEFAULT_IMPORT_PRESET);
      applySelectionForPreset(result, DEFAULT_IMPORT_PRESET);
      return;
    }
    if (prevSystemsLenRef.current !== n) {
      prevSystemsLenRef.current = n;
      applySelectionForPreset(result, importPresetRef.current);
    }
  }, [result, result?.audit_id, result?.intent?.systems?.length, applySelectionForPreset]);

  const setImportPresetAndSelect = useCallback(
    (preset: ImportFilterPreset) => {
      setImportPreset(preset);
      if (result?.intent?.systems?.length) applySelectionForPreset(result, preset);
    },
    [result, applySelectionForPreset]
  );

  const addFiles = useCallback((incoming: File[]) => {
    setError(null);
    const allowed = incoming.filter((f) => isAcceptedImageOrPdfFile(f));
    const oversized = allowed.filter((f) => f.size > MAX_FILE_BYTES);
    if (oversized.length > 0) {
      setError(
        `Bestand te groot (max. ${MAX_FILE_BYTES / (1024 * 1024)} MB per bestand): ${oversized.map((f) => f.name).join(', ')}`
      );
    }
    const ok = allowed.filter((f) => f.size <= MAX_FILE_BYTES);
    setFiles((prev) => [...prev, ...ok].slice(0, MAX_FILES));
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const items = Array.from(e.dataTransfer.files);
      addFiles(items);
    },
    [addFiles]
  );

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const items = Array.from(e.target.files ?? []);
    addFiles(items);
    e.target.value = '';
  };

  const removeFile = (i: number) => setFiles((prev) => prev.filter((_, j) => j !== i));

  const ctId = customerTypeId || 'particulier';

  const drawingProfileHint = useMemo(() => {
    const pt = projectType.trim() || 'renovatie';
    const bt = buildingType.trim() || 'woning';
    const ctLabel = CUSTOMER_TYPE_OPTIONS.find((o) => o.id === ctId)?.label ?? ctId;
    return getJourneyProfileHint({
      projectTypeId: pt,
      buildingTypeId: bt,
      customerTypeId: ctId,
      projectTypeLabel: projectType.trim() || pt,
      buildingTypeLabel: buildingType.trim() || bt,
      customerTypeLabel: ctLabel,
    });
  }, [projectType, buildingType, ctId]);

  const persistCache = async (r: AnalysisResult) => {
    const cache = analysisResultToCache(r);
    await api.updateProject(
      projectId,
      { tenant_id: tenantId, drawing_analysis_cache: cache },
      { revisionSeq: rev() }
    );
    onProposalAttached?.();
  };

  const handleClearPersistedAnalysis = async () => {
    setLoading(true);
    setError(null);
    try {
      await api.updateProject(
        projectId,
        { tenant_id: tenantId, clear_drawing_analysis_cache: true },
        { revisionSeq: rev() }
      );
      setResult(null);
      onProposalAttached?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Analysecache wissen mislukt');
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyze = async () => {
    const libPicked = analyzableLibrary.filter((d) => selectedLibraryDocIds.has(d.doc_id));
    if (files.length === 0 && libPicked.length === 0) {
      setError('Kies nieuwe bestanden óf vink documenten uit de bibliotheek aan.');
      return;
    }
    const tooBig = files.filter((f) => f.size > MAX_FILE_BYTES);
    if (tooBig.length > 0) {
      setError(
        `Eén of meer bestanden zijn groter dan ${MAX_FILE_BYTES / (1024 * 1024)} MB. Verwijder ze of comprimeer de PDF.`
      );
      return;
    }
    const pc = normalizePostalCode(postalCode);
    if (pc && !/^\d{4}[A-Z]{2}$/.test(pc)) {
      setError('Postcode in projectcontext ongeldig (1234AB). Pas context aan en sla op.');
      return;
    }

    setLoading(true);
    setShowAnalyzeNarrative(true);
    setError(null);
    setResult(null);

    try {
      const documents: Array<{ type: 'image' | 'pdf'; url: string; label?: string }> = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (file.type === 'application/pdf') {
          const imageFiles = await pdfToImageFiles(file);
          for (let j = 0; j < imageFiles.length; j++) {
            const { url } = await api.uploadDocument(imageFiles[j], tenantId);
            documents.push({ type: 'image', url, label: `${file.name} p.${j + 1}` });
          }
        } else {
          const { url } = await api.uploadDocument(file, tenantId);
          documents.push({ type: 'image', url, label: file.name });
        }
      }

      if (onAfterUploadToLibrary && documents.length > 0) {
        await onAfterUploadToLibrary(
          documents.map((d) => ({
            url: d.url,
            label: d.label,
            content_type: d.type === 'pdf' ? 'application/pdf' : 'image/*',
          }))
        );
      }

      if (libPicked.length > 0) {
        const fromLib = await libraryDocumentsToAnalyzeRefs(libPicked, tenantId);
        documents.push(...fromLib);
      }

      if (documents.length > MAX_ANALYZE_DOCUMENTS) {
        setError(
          `Te veel pagina’s/platen voor één analyse (max. ${MAX_ANALYZE_DOCUMENTS}). Kies minder PDF-pagina’s, minder bibliotheekdocumenten of splits in twee analyses.`
        );
        setLoading(false);
        setShowAnalyzeNarrative(false);
        return;
      }

      const res = (await api.analyzeDocuments({
        documents,
        customer_wishes: customerWishes || undefined,
        tenant_id: tenantId,
        project_context: {
          project_type: projectType,
          building_type: buildingType,
          postal_code: pc || undefined,
          index_year: indexYear,
          customer_type: ctId,
          template_id: templateId?.trim() || undefined,
        },
      })) as AnalysisResult;

      setResult(res);
      await persistCache(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Analyse mislukt');
    } finally {
      setLoading(false);
      setShowAnalyzeNarrative(false);
    }
  };

  const toggleLine = (idx: number) => {
    setSelectedLineIdx((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const selectAllLines = () => {
    const n = result?.intent?.systems?.length ?? 0;
    setSelectedLineIdx(new Set(Array.from({ length: n }, (_, i) => i)));
  };

  const selectNoLines = () => setSelectedLineIdx(new Set());

  const handleAttachProposalToProject = async () => {
    if (!result?.intent?.systems?.length) return;
    const indices = [...selectedLineIdx].sort((a, b) => a - b);
    if (indices.length === 0) {
      setError('Selecteer minimaal één post om te importeren, of kies “Alles selecteren”.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const proposed_systems: ProjectSystemLine[] = [];

      for (const idx of indices) {
        const s = result.intent.systems[idx] as AnalysisSystemLine | undefined;
        if (!s) continue;
        const system_id = s.system_id?.trim();
        if (!system_id) continue;

        let variant_id = s.variant_id?.trim() ?? null;
        if (!variant_id) {
          try {
            const { variants } = await api.getSystemVariants(system_id, tenantId);
            variant_id = variants?.[0]?.variant_id ?? null;
          } catch {
            variant_id = null;
          }
        }
        if (!variant_id) continue;

        const qtyRaw = result.intent.quantity_estimates?.[system_id] ?? s.quantity_hint;
        const quantity = typeof qtyRaw === 'number' && qtyRaw > 0 ? qtyRaw : 1;

        const bc = typeof s.bouwcluster === 'string' ? s.bouwcluster.trim() : '';
        const vd = typeof s.verdieping === 'string' ? s.verdieping.trim() : '';
        const rm = typeof s.ruimte_of_nummer === 'string' ? s.ruimte_of_nummer.trim() : '';

        const line: ProjectSystemLine = { system_id, variant_id, quantity };
        if (bc) line.building_part = bc;
        if (vd) line.floor = vd;
        if (rm) line.room_number = rm;
        proposed_systems.push(line);
      }

      if (proposed_systems.length === 0) {
        setError(
          'Geen enkele geselecteerde regel kon aan het prijsboek worden gekoppeld. Controleer de systeemnamen of kies andere posten.'
        );
        setLoading(false);
        return;
      }

      const importedIdx = new Set(indices);
      const trimmed = trimAnalysisAfterImport(result, importedIdx);
      const mergedProposal = mergeDraftProposal(
        existingDrawingProposal,
        proposed_systems,
        result.audit_id,
        result.analysis_summary
      );

      const body: {
        tenant_id: string;
        drawing_proposal: DrawingProposal;
        drawing_analysis_cache?: DrawingAnalysisCache;
        clear_drawing_analysis_cache?: boolean;
      } = {
        tenant_id: tenantId,
        drawing_proposal: mergedProposal,
      };
      if (trimmed && trimmed.intent.systems.length > 0) {
        body.drawing_analysis_cache = analysisResultToCache(trimmed);
      } else {
        body.clear_drawing_analysis_cache = true;
      }

      await api.updateProject(projectId, body, { revisionSeq: rev() });

      setFiles([]);
      setCustomerWishes('');
      if (trimmed && trimmed.intent.systems.length > 0) {
        setResult(trimmed);
      } else {
        setResult(null);
      }
      onProposalAttached?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Voorstel opslaan mislukt');
    } finally {
      setLoading(false);
    }
  };

  const uploadId = `drawing-upload-${projectId}`;
  const nSystems = result?.intent.systems?.length ?? 0;
  const nSelected = selectedLineIdx.size;
  const canAttach =
    result &&
    nSystems > 0 &&
    nSelected > 0 &&
    [...selectedLineIdx].some((idx) => {
      const s = result.intent.systems[idx] as AnalysisSystemLine | undefined;
      return !!s?.system_id?.trim();
    });

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-sky/40 bg-sky/10 px-3 py-3 text-xs leading-relaxed text-text-muted">
        <p className="mb-2 flex items-start gap-2 font-medium text-text">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-sky" aria-hidden />
          Zo werkt het (kort)
        </p>
        <ol className="list-decimal space-y-1 pl-4">
          <li>
            Nieuwe upload <strong>of</strong> documenten uit je <strong>documentenbibliotheek</strong> (hieronder) → analyse
            blijft op het dossier staan tot je opnieuw analyseert, wist, of alle posten uit de lijst hebt geïmporteerd.
          </li>
          <li>
            Kies welke posten je importeert (alles of een deel). Je kunt later opnieuw importeren uit dezelfde analyse;
            het conceptvoorstel wordt <strong>aangevuld</strong> totdat er geen posten meer over zijn.
          </li>
          <li>Controleer op tab Posten en keur goed wanneer je klaar bent — dan komen regels in je begroting.</li>
        </ol>
        <p className="mt-2">
          <Link to="/help/tekening" className="font-medium text-primary hover:underline">
            Uitgebreide uitleg
          </Link>
          {' · '}
          <Link to="/troubleshoot" className="font-medium text-primary hover:underline">
            Problemen oplossen
          </Link>
        </p>
      </div>
      <p className="text-xs leading-relaxed text-text-muted">
        <span className="font-medium text-text">Profiel voor deze analyse: </span>
        {drawingProfileHint}
      </p>
      <p className="text-xs text-text-muted">
        <span className="font-medium text-text">Analyse: </span>
        Zelfde diepte voor alle klanttypes (volledige koppeling met de prijsbibliotheek). Je <strong>klanttype</strong>{' '}
        stuurt vooral toon, vervolgvragen en het <strong>importadvies</strong> per regel (kern / aanbevolen / aanvullend)
        na de analyse.
      </p>

      {analyzableLibrary.length > 0 ? (
        <div className="rounded-lg border border-border/80 bg-surface-card/60 px-3 py-3">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <p className="text-sm font-medium text-text">Documenten uit bibliotheek meenemen</p>
            <Tooltip
              iconOnly
              label="Uitleg bibliotheek en analyse"
              content={
                <>
                  Vink platen aan die al op dit dossier staan (tab Gegevens → Documentenbibliotheek). Je hoeft ze niet
                  opnieuw te uploaden. PDF’s worden intern weer naar pagina-afbeeldingen omgezet. Geef bestanden in de
                  bibliotheek een duidelijke naam (potlood → bewerken) — bijv.{' '}
                  <span className="font-mono text-xs">01-bestaand-BG</span>,{' '}
                  <span className="font-mono text-xs">02-ontwerp-1e-verdieping</span> — dat helpt het model met fase en
                  verdieping. Max. {MAX_ANALYZE_DOCUMENTS} platen per analyse (telt PDF-pagina’s mee).
                </>
              }
            />
          </div>
          <ul className="max-h-48 space-y-2 overflow-y-auto text-sm">
            {analyzableLibrary.map((d) => (
              <li key={d.doc_id}>
                <label className="flex cursor-pointer items-start gap-2 rounded-md border border-transparent px-1 py-1 hover:bg-surface/80">
                  <input
                    type="checkbox"
                    checked={selectedLibraryDocIds.has(d.doc_id)}
                    onChange={() => {
                      setSelectedLibraryDocIds((prev) => {
                        const n = new Set(prev);
                        if (n.has(d.doc_id)) n.delete(d.doc_id);
                        else n.add(d.doc_id);
                        return n;
                      });
                    }}
                    className="mt-1 h-4 w-4 shrink-0 rounded border-border text-primary"
                  />
                  <span className="min-w-0 text-text-muted">
                    <span className="font-medium text-text">{d.label || 'Zonder naam'}</span>
                    {d.content_type ? (
                      <span className="ml-2 text-xs opacity-80">({d.content_type})</span>
                    ) : null}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <label
        htmlFor={uploadId}
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
        className="block cursor-pointer rounded-lg border-2 border-dashed border-border bg-surface p-6 text-center transition hover:border-primary/50"
      >
        <input
          type="file"
          accept={ACCEPT}
          multiple
          onChange={handleFileInput}
          className="sr-only"
          id={uploadId}
        />
        <Upload className="mx-auto mb-2 h-10 w-10 text-text-muted" aria-hidden />
        <p className="mb-2 text-sm text-text-muted">Sleep bestanden hierheen of klik om te selecteren</p>
        <p className="mb-2 flex flex-wrap items-center justify-center gap-2 text-xs text-text-muted">
          <span>
            PNG, JPEG, WebP, GIF of PDF (max {MAX_FILES} bestanden, 10 MB per bestand)
          </span>
          <Tooltip
            iconOnly
            label="Tip bestandsnamen"
            content={
              <>
                Gebruik herkenbare namen vóór upload, bijv. <span className="font-mono">sloop-plattegrond-BG.pdf</span>,{' '}
                <span className="font-mono">nieuwe-situatie-elektra.pdf</span>. Je kunt namen ook achteraf wijzigen in de
                documentenbibliotheek (potlood). Duidelijke namen verbeteren de analyse.
              </>
            }
          />
        </p>
        <span className="inline-flex rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white pointer-events-none">
          Bestanden kiezen
        </span>
      </label>

      {files.length > 0 && (
        <ul className="space-y-2">
          {files.map((f, i) => (
            <li key={i} className="flex items-center justify-between rounded bg-surface-card px-3 py-2">
              <span className="flex items-center gap-2 text-sm">
                <FileText className="h-4 w-4" />
                {f.name} ({(f.size / 1024).toFixed(1)} KB)
              </span>
              <button
                type="button"
                onClick={() => removeFile(i)}
                className="cursor-pointer text-red-600 hover:underline"
              >
                Verwijderen
              </button>
            </li>
          ))}
        </ul>
      )}

      <div>
        <label className="mb-1 block text-sm font-medium text-text">Klantwensen (optioneel)</label>
        <textarea
          value={customerWishes}
          onChange={(e) => setCustomerWishes(e.target.value)}
          placeholder="Extra informatie voor de analyse…"
          rows={2}
          className="w-full rounded-lg border border-border px-3 py-2 text-text"
        />
      </div>

      <p className="text-xs text-text-muted">
        Projecttype, gebouw, postcode en klanttype komen uit <strong>projectcontext</strong> (boven). Pas die eerst aan
        indien nodig en sla op vóór analyse.
      </p>

      {error && <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>}

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => void handleAnalyze()}
          disabled={loading || (files.length === 0 && selectedLibraryDocIds.size === 0)}
          className="flex cursor-pointer items-center gap-2 rounded-lg bg-primary px-6 py-2.5 font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Analyseren…
            </>
          ) : result ? (
            'Opnieuw analyseren (vervangt opgeslagen analyse)'
          ) : (
            'Tekeningen analyseren'
          )}
        </button>
        {result || persistedAnalysisCache ? (
          <button
            type="button"
            onClick={() => void handleClearPersistedAnalysis()}
            disabled={loading}
            className="cursor-pointer rounded-lg border border-border px-4 py-2.5 text-sm font-medium text-text-muted hover:bg-surface-card disabled:opacity-50"
          >
            Opgeslagen analyse wissen
          </button>
        ) : null}
      </div>

      {showAnalyzeNarrative && loading ? (
        <div
          className="overflow-hidden rounded-xl border border-primary/25 bg-gradient-to-r from-primary/[0.07] via-surface-card to-primary/[0.05] px-4 py-3 shadow-sm"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          <div className="flex items-start gap-3">
            <span
              className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/15"
              aria-hidden
            >
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
            </span>
            <div className="min-w-0 flex-1 pt-0.5">
              <p className="text-xs font-medium uppercase tracking-wide text-primary/90">Bezig met analyseren</p>
              <p
                key={analyzeNarrativeIdx}
                className="animate-drawing-narrative-in mt-1 text-sm leading-snug text-text"
              >
                {ANALYSIS_NARRATIVE_STEPS[analyzeNarrativeIdx]}
              </p>
              <p className="mt-2 text-[11px] text-text-muted">
                Even geduld — grote tekeningen of veel pagina’s kunnen langer duren.
              </p>
            </div>
          </div>
        </div>
      ) : null}

      {result && (
        <div className="space-y-4 rounded-lg border border-border bg-surface-card p-5">
          <h3 className="font-semibold text-text">Analyse-resultaat</h3>
          <p className="text-xs text-text-muted">{getDrawingAnalysisAudienceHint(ctId)}</p>
          {result.analysis_depth === 'full' ? (
            <p className="text-xs font-medium text-primary">Modus: volledige catalogusanalyse (alle klanttypes)</p>
          ) : result.analysis_depth === 'deep' ? (
            <p className="text-xs font-medium text-text-muted">Modus: eerdere analyse (diep) — opnieuw analyseren voor nieuw model</p>
          ) : (
            <p className="text-xs font-medium text-text-muted">Modus: eerdere analyse (standaard) — opnieuw analyseren aanbevolen</p>
          )}
          {persistedAnalysisCache?.saved_at ? (
            <p className="text-[11px] text-text-muted">
              Opgeslagen op dit dossier: {new Date(persistedAnalysisCache.saved_at).toLocaleString('nl-NL')}
            </p>
          ) : null}
          {result.analysis_summary && <p className="text-sm text-text-muted">{result.analysis_summary}</p>}
          {(() => {
            const b = betrouwbaarheidAlsNlTekst(result.confidence);
            return (
              <div className="rounded-lg border border-border/80 bg-surface/80 px-3 py-2 text-sm">
                <p className="font-medium text-text">{b.kop}</p>
                <p className="mt-1 text-text-muted">{b.uitleg}</p>
                <details className="mt-2 text-xs text-text-muted">
                  <summary className="cursor-pointer font-medium text-text">Technische score (model)</summary>
                  <p className="mt-1">
                    Interne schaal: {(result.confidence * 100).toFixed(0)} op 100 — alleen ter vergelijking tussen
                    analyses, geen garantie op de werkelijkheid.
                  </p>
                </details>
              </div>
            );
          })()}
          {result.analysis_depth === 'full' &&
          (!result.drawing_extras?.ruimtes_inventaris ||
            result.drawing_extras.ruimtes_inventaris.length === 0) && (
            <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
              In dit antwoord ontbreekt de ruimtelijst (<code className="rounded bg-black/5 px-1 dark:bg-white/10">ruimtes_inventaris</code>
              ). Probeer opnieuw te analyseren; bij herhaling kun je in klantwensen expliciet vragen om alle gelabelde ruimtes in een tabel.
            </p>
          )}
          {result.drawing_extras?.ruimtes_inventaris && result.drawing_extras.ruimtes_inventaris.length > 0 ? (
            <div className="overflow-x-auto">
              <p className="mb-1 text-sm font-medium text-text">Ruimtes op de tekening (inventaris)</p>
              <p className="mb-2 text-xs text-text-muted">
                Minimaal overzicht per zichtbare ruimte: wooneenheid, verdieping, nummer en naam — los van prijsboekposten.
              </p>
              {result.analysis_meta &&
              (result.analysis_meta.ruimtes_inventory_completion_applied ||
                result.analysis_meta.ruimtes_threshold_reference != null) ? (
                <p className="mb-2 text-[11px] text-text-muted">
                  {result.analysis_meta.ruimtes_inventory_completion_applied
                    ? `Ruimtelijst aangevuld met extra controlepass (${result.analysis_meta.ruimtes_count_pass1 ?? '—'} → ${result.analysis_meta.ruimtes_count_final ?? result.drawing_extras?.ruimtes_inventaris?.length ?? '—'} rijen; richtwaarde ≥${result.analysis_meta.ruimtes_threshold_reference ?? '—'}).`
                    : `Eerste pass: ${result.analysis_meta.ruimtes_count_final ?? result.drawing_extras?.ruimtes_inventaris?.length ?? '—'} ruimterijen (richtwaarde ≥${result.analysis_meta.ruimtes_threshold_reference}).`}
                </p>
              ) : null}
              {result.analysis_meta?.inventory_conflicts &&
              result.analysis_meta.inventory_conflicts.length > 0 ? (
                <div
                  className="mb-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100"
                  role="status"
                >
                  <p className="font-medium">Inventaris: mogelijke tegenstrijdigheden</p>
                  <ul className="mt-1 list-inside list-disc space-y-0.5">
                    {result.analysis_meta.inventory_conflicts.map((c, i) => (
                      <li key={i}>{c.message_nl}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {result.analysis_meta?.systems_inventory_autofill_note_nl ? (
                <div
                  className="mb-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-xs text-text"
                  role="status"
                >
                  <p className="font-medium">Aanvulling posten (inventarisdekking)</p>
                  <p className="mt-1 text-text-muted">
                    {result.analysis_meta.systems_inventory_autofill_note_nl}
                  </p>
                </div>
              ) : null}
              {result.analysis_meta?.inventory_engine_warnings_nl &&
              result.analysis_meta.inventory_engine_warnings_nl.length > 0 ? (
                <div
                  className="mb-2 rounded-md border border-border/80 bg-surface px-3 py-2 text-xs text-text-muted"
                  role="status"
                >
                  <p className="font-medium text-text">Controles (deterministisch)</p>
                  <ul className="mt-1 list-inside list-disc space-y-0.5">
                    {result.analysis_meta.inventory_engine_warnings_nl.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
              <table className="w-full min-w-[64rem] border-collapse text-left text-sm">
                <thead>
                  <tr className="border-b border-border bg-surface/80 text-xs font-medium text-text-muted">
                    <th className="px-2 py-2">Woning / eenheid</th>
                    <th className="px-2 py-2">Verdieping</th>
                    <th className="px-2 py-2">Ruimtenr.</th>
                    <th className="px-2 py-2">Ruimtenaam</th>
                    <th className="px-2 py-2 whitespace-nowrap">m²</th>
                    <th className="px-2 py-2 whitespace-nowrap">Wand m¹</th>
                    <th className="px-2 py-2 whitespace-nowrap">H m</th>
                    <th className="px-2 py-2">Deuren</th>
                    <th className="px-2 py-2">Kozijnen</th>
                    <th className="px-2 py-2 whitespace-nowrap">Bron</th>
                    <th className="px-2 py-2">Document</th>
                    <th className="px-2 py-2">Opmerking</th>
                  </tr>
                </thead>
                <tbody>
                  {result.drawing_extras.ruimtes_inventaris.map((r, i) => (
                    <tr key={i} className="border-b border-border/70 text-text">
                      <td className="px-2 py-1.5 align-top">{r.woning_of_eenheid}</td>
                      <td className="px-2 py-1.5 align-top">{r.verdieping}</td>
                      <td className="px-2 py-1.5 align-top font-mono text-xs">{r.ruimtenummer ?? '—'}</td>
                      <td className="px-2 py-1.5 align-top font-medium">{r.ruimte_naam}</td>
                      <td className="px-2 py-1.5 align-top text-xs tabular-nums">
                        {formatInventoryMeasure(r.oppervlakte_m2)}
                      </td>
                      <td className="px-2 py-1.5 align-top text-xs tabular-nums">
                        {formatInventoryMeasure(r.wandlengte_totaal_m1)}
                      </td>
                      <td className="px-2 py-1.5 align-top text-xs tabular-nums">
                        {formatInventoryMeasure(r.plafondhoogte_m)}
                      </td>
                      <td className="max-w-[10rem] px-2 py-1.5 align-top text-xs text-text-muted">
                        {r.aantal_deuren != null ? r.aantal_deuren : '—'}
                        {r.deuren_omschrijving
                          ? ` · ${truncateInvText(r.deuren_omschrijving, 32)}`
                          : ''}
                      </td>
                      <td className="max-w-[10rem] px-2 py-1.5 align-top text-xs text-text-muted">
                        {r.aantal_raam_kozijn != null ? r.aantal_raam_kozijn : '—'}
                        {r.kozijnen_omschrijving
                          ? ` · ${truncateInvText(r.kozijnen_omschrijving, 32)}`
                          : ''}
                      </td>
                      <td className="px-2 py-1.5 align-top text-xs text-text-muted">
                        {r.meetwaarden_bron === 'tekening'
                          ? 'Tekening'
                          : r.meetwaarden_bron === 'geschat'
                            ? 'Geschat'
                            : '—'}
                      </td>
                      <td className="px-2 py-1.5 align-top text-xs text-text-muted">
                        {typeof r.doc_index === 'number' ? r.doc_index + 1 : '—'}
                      </td>
                      <td className="max-w-[12rem] px-2 py-1.5 align-top text-xs text-text-muted">
                        {truncateInvText(r.opmerking ?? '', 80) || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          {result.document_insights && result.document_insights.length > 0 ? (
            <div>
              <p className="mb-1 text-sm font-medium text-text">Bevindingen per document</p>
              <ul className="space-y-2 text-sm text-text-muted">
                {result.document_insights.map((di, i) => (
                  <li key={i} className="rounded-md border border-border/60 bg-surface px-2 py-2">
                    {typeof di.doc_index === 'number' ? (
                      <p className="mb-1 text-xs font-medium text-text">Document {di.doc_index + 1}</p>
                    ) : null}
                    <ul className="list-inside list-disc space-y-0.5">
                      {(di.findings ?? []).map((f, j) => (
                        <li key={j}>{f}</li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {result.drawing_extras?.overige_waarneembare_elementen &&
          result.drawing_extras.overige_waarneembare_elementen.length > 0 ? (
            <div>
              <p className="mb-1 text-sm font-medium text-text">Overige waarneembare onderdelen op de tekening</p>
              <ul className="list-inside list-disc text-sm text-text-muted">
                {result.drawing_extras.overige_waarneembare_elementen.map((t, i) => (
                  <li key={i}>{t}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {result.drawing_extras?.afkortingen_gezien && result.drawing_extras.afkortingen_gezien.length > 0 ? (
            <div>
              <p className="mb-1 text-sm font-medium text-text">Afkortingen en codes (interpretatie)</p>
              <ul className="space-y-1 text-sm text-text-muted">
                {result.drawing_extras.afkortingen_gezien.map((a, i) => (
                  <li key={i}>
                    <span className="font-mono text-text">{a.code ?? '—'}</span>
                    {a.betekenis_nl ? <> — {a.betekenis_nl}</> : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {result.clarification_questions?.length > 0 && (
            <div>
              <p className="mb-1 text-sm font-medium text-text">Vervolgvragen</p>
              <ul className="list-inside list-disc text-sm text-text-muted">
                {result.clarification_questions.map((q, i) => (
                  <li key={i}>{q}</li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() => {
                  const lines = [
                    'Ik wil de tekeninganalyse verder uitwerken in dit dossier.',
                    result.analysis_summary ? `Korte samenvatting van de analyse: ${result.analysis_summary}` : null,
                    'Vervolgvragen waar ik hulp bij wil:',
                    ...result.clarification_questions.map((q, n) => `${n + 1}. ${q}`),
                    result.audit_id ? `(Referentie analyse: ${result.audit_id})` : null,
                  ].filter(Boolean);
                  openAssistantWithDraft(lines.join('\n'));
                }}
                className="mt-2 cursor-pointer rounded-md border border-primary/40 bg-primary/5 px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/10"
              >
                Open assistent met deze vragen
              </button>
              <p className="mt-1 text-[11px] text-text-muted">
                De assistent krijgt geen automatische kopie van je platen; beschrijf zo nodig kort wat je nog mist.
              </p>
            </div>
          )}
          {result.intent.systems?.length > 0 && (
            <div>
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-medium text-text">Gevonden posten uit het prijsboek</p>
                <div className="flex flex-wrap gap-2 text-xs">
                  <button type="button" onClick={selectAllLines} className="font-medium text-primary hover:underline">
                    Alles selecteren
                  </button>
                  <span className="text-text-muted">·</span>
                  <button type="button" onClick={selectNoLines} className="font-medium text-primary hover:underline">
                    Niets selecteren
                  </button>
                </div>
              </div>
              <div className="mb-3 rounded-lg border border-border/80 bg-surface/90 px-3 py-2">
                <p className="mb-2 text-xs font-medium text-text">Advies voor import (op basis van je klanttype)</p>
                <p className="mb-2 text-xs text-text-muted">{importFilterHintForCustomerType(ctId)}</p>
                <div className="flex flex-wrap gap-2">
                  {(['kern', 'kern_aanbevolen', 'alles'] as const).map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setImportPresetAndSelect(p)}
                      className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
                        importPreset === p
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border text-text-muted hover:border-primary/40 hover:text-text'
                      }`}
                    >
                      {importPresetLabel(p)}
                    </button>
                  ))}
                </div>
              </div>
              <p className="mb-2 text-xs text-text-muted">
                Vink posten aan om ze <strong>in bulk of per stuk</strong> als conceptvoorstel op dit dossier te zetten (
                {nSelected} van {nSystems} geselecteerd). Elke regel heeft een label Kern / Aanbevolen / Aanvullend.
              </p>
              <ul className="space-y-3 text-sm">
                {result.intent.systems.map((raw, i) => {
                  const s = raw as AnalysisSystemLine;
                  const pad = [s.hoofdgroep_nl, s.subgroep_nl].filter(Boolean).join(' › ');
                  const titel =
                    s.detail_nl?.trim() ||
                    formatTechnischeIdVoorWeergave(s.system_id);
                  const variantWeergave = formatTechnischeIdVoorWeergave(s.variant_id);
                  const hoev = hoeveelheidTekst(
                    s.quantity_hint,
                    result.intent.quantity_estimates,
                    s.system_id
                  );
                  const loc = [s.bouwcluster, s.verdieping, s.ruimte_of_nummer].filter(Boolean).join(' · ');
                  const checked = selectedLineIdx.has(i);
                  const tier = normalizeImportTier(s.import_tier);
                  return (
                    <li
                      key={i}
                      className={`rounded-lg border px-3 py-2.5 leading-snug ${
                        checked ? 'border-primary/50 bg-primary/5' : 'border-border/70 bg-surface'
                      } text-text-muted`}
                    >
                      <label className="flex cursor-pointer gap-3">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleLine(i)}
                          className="mt-1 h-4 w-4 shrink-0 rounded border-border text-primary"
                        />
                        <span className="min-w-0 flex-1">
                          <div className="mb-1 flex flex-wrap items-center gap-2">
                            <span
                              className={`inline-flex rounded px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${importTierBadgeClass(tier)}`}
                            >
                              {importTierLabelNl(tier)}
                            </span>
                          </div>
                          {pad ? <p className="text-xs font-medium text-text-muted">{pad}</p> : null}
                          <p className="font-medium text-text">{titel}</p>
                          {loc ? <p className="mt-0.5 text-xs text-text-muted">{loc}</p> : null}
                          {s.reasoning?.trim() ? (
                            <p className="mt-1 text-xs italic text-text-muted">Onderbouwing: {s.reasoning.trim()}</p>
                          ) : null}
                          <p className="mt-1 text-xs">
                            Variant: <span className="text-text">{variantWeergave}</span>
                            {' · '}
                            Hoeveelheid: <span className="text-text">{hoev}</span>
                            {typeof s.confidence === 'number' ? (
                              <>
                                {' · '}
                                Zekerheid model:{' '}
                                <span className="text-text">{(Math.max(0, Math.min(1, s.confidence)) * 100).toFixed(0)}%</span>
                              </>
                            ) : null}
                          </p>
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          <p className="text-sm text-text-muted">
            Je zet een <strong>conceptvoorstel</strong> op het dossier voor de <strong>nu geselecteerde</strong> posten.
            Resterende posten blijven in je opgeslagen analyse staan voor een volgende import. Er is nog geen vaste
            begroting tot je op <strong>Posten</strong> goedkeurt.
          </p>
          <p className="text-xs text-text-muted">
            Ontbreekt de variant in de analyse, dan kiezen we bij opslaan de eerste passende variant uit je prijsboek —
            controleer daarna op Posten.
          </p>
          <button
            type="button"
            onClick={() => void handleAttachProposalToProject()}
            disabled={loading || !canAttach}
            className="cursor-pointer rounded-lg bg-primary px-6 py-2.5 font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading
              ? 'Bezig…'
              : nSelected === nSystems
                ? 'Voorstel op dit dossier zetten (alle posten)'
                : `Voorstel op dit dossier zetten (${nSelected} post${nSelected === 1 ? '' : 'en'})`}
          </button>
        </div>
      )}
    </div>
  );
}
